import { type ReactNode } from "react";

import { t } from "i18n/i18n";
import { MinervaLogoIcon } from "mds/icons";
import { usePromiseEffect } from "store/store-hooks";
import { reportApiFailure } from "utils/api-errors";
import { rollbarAndLogWarning } from "utils/logger";
import { areWaffleFlagsLoaded, waitForWaffleFlags } from "utils/waffle-flags";

type Props = { children: ReactNode };

// Shows a loading state until the wafflejs snapshot is available, then renders children.
export function WaffleFlagReadyBoundary({ children }: Props) {
  const flagsAlreadyLoaded = areWaffleFlagsLoaded();
  const { isLoading } = usePromiseEffect(
    async () => {
      try {
        const loaded = await waitForWaffleFlags();
        if (!loaded) {
          rollbarAndLogWarning(
            "Waffle flags failed to load before render; flag checks will return false until the page is reloaded.",
          );
        }
      } catch (error) {
        reportApiFailure("Failed to load waffle flags", error);
      }
    },
    [],
    { initialLoading: !flagsAlreadyLoaded, skip: flagsAlreadyLoaded },
  );

  if (isLoading) {
    return (
      <div className="flex h-full w-full animate-pulse flex-col items-center justify-center gap-[12px]">
        <MinervaLogoIcon height={96} width={96} />
        <div className="text-2xl">{t("common.loading_ellipsis")}</div>
      </div>
    );
  }

  return children;
}
